import { CLIFF_HALF, mesaContour, mesaEdge, mesaHeight, mesaPlateau, mesaShape, type Mesa, type Ramp } from "./landforms.ts";
import type { Boulder, Dock, Island, Prop, Stair } from "./world.ts";

/**
 * Splinter Bastion: a pirate holdout on a sandstone mesa that rises straight out of
 * the shallows. A walled timber yard sits on a low shelf facing Driftwood Landing; a
 * long scaffold stair climbs the cliff to a palm-topped plateau crowned by a beacon
 * fire, and a stilted lookout stands on a sandbar offshore. Island-local metres;
 * +x faces the outpost.
 */

const CX = -600, CZ = -1250;

const MAIN: Mesa = {
  x: -4, z: 6, rx: 40, rz: 48, power: 3.2, top: 21, dome: .8, edge: 2.4,
  wobble: [{ k: 3, amp: .04, phase: .9 }, { k: 5, amp: .035, phase: .7 }, { k: 9, amp: .022, phase: 2.1 }, { k: 14, amp: .012, phase: 4 }],
};
const YARD: Mesa = {
  x: 26, z: -44, rx: 34, rz: 21, power: 2.8, top: 5.6, dome: .2, edge: 2, sandy: true,
  wobble: [{ k: 4, amp: .04, phase: 1.1 }, { k: 7, amp: .02, phase: .3 }],
};

/** First point along a ray from the yard centre where the outline reaches `inset` metres inside its cliff. */
function yardRim(dx: number, dz: number, inset: number): { x: number; z: number } {
  const len = Math.hypot(dx, dz), target = 1 - mesaEdge(YARD) / 2 - inset / Math.min(YARD.rx, YARD.rz);
  for (let d = 0; d < 80; d += .05) {
    const x = YARD.x + dx / len * d, z = YARD.z + dz / len * d;
    if (mesaShape(YARD, x, z) >= target) return { x, z };
  }
  throw new Error("yard rim not found");
}

// The gate faces south-east toward the jetty; the ramp drops from it into the shallows.
const GATE_DIR = { x: .3, z: -1 };
const gateLen = Math.hypot(GATE_DIR.x, GATE_DIR.z);
const GATE = yardRim(GATE_DIR.x, GATE_DIR.z, 3);
const RAMP_START = yardRim(GATE_DIR.x, GATE_DIR.z, 1.5);
const RAMP: Ramp = {
  x0: RAMP_START.x, z0: RAMP_START.z, h0: YARD.top,
  x1: RAMP_START.x + GATE_DIR.x / gateLen * 14, z1: RAMP_START.z + GATE_DIR.z / gateLen * 14, h1: 1.05,
  width: 4.6,
};

// The stair climbs west along the main cliff's south face. Its landing overlaps the
// cliff colliders slightly so the top step meets the plateau without a gap.
const STAIR_X0 = 28, STAIR_TREAD = .6, STAIR_WIDTH = 2.6, STAIR_LANDING = 2.8;
const stairTop = mesaPlateau(MAIN, 1 - mesaEdge(MAIN));
const stairEndGuess = STAIR_X0 - 40 * STAIR_TREAD - STAIR_LANDING;
const landingFace = Math.max(...mesaContour(MAIN, 0, .25)
  .filter((p) => p.nz < -.5 && p.x > stairEndGuess - CLIFF_HALF && p.x < stairEndGuess + STAIR_LANDING + CLIFF_HALF)
  .map((p) => p.z - CLIFF_HALF));
const stairZ = landingFace + .25 - STAIR_WIDTH / 2;
const stairBase = mesaHeight(YARD, STAIR_X0, stairZ);
const stairSteps = Math.ceil((stairTop - stairBase) / .4);
const stairEnd = STAIR_X0 - stairSteps * STAIR_TREAD - STAIR_LANDING;
const STAIR: Stair = {
  x: STAIR_X0, z: stairZ, dirX: -1, dirZ: 0, width: STAIR_WIDTH,
  base: stairBase, top: stairTop, steps: stairSteps, tread: STAIR_TREAD, landing: STAIR_LANDING,
};

// The jetty runs straight on from the foot of the ramp.
const DOCK: Dock = { x0: RAMP.x1 - 1.5, x1: RAMP.x1 + 1.5, z0: RAMP.z1 - 22, z1: RAMP.z1 + .3, top: 1.25 };

/** Deterministic jitter in [-1, 1] for authored placement. */
function jitter(i: number, salt: number): number {
  const v = Math.sin(i * 127.1 + salt * 311.7) * 43758.5453;
  return (v - Math.floor(v)) * 2 - 1;
}

/** Island-local point at fractions of the main mesa's radii. */
function onTop(fx: number, fz: number): { x: number; z: number } {
  return { x: MAIN.x + fx * MAIN.rx, z: MAIN.z + fz * MAIN.rz };
}

const LOOKOUT = { x: 45, z: 45 };

const BOULDERS: Boulder[] = [
  // Tumbled blocks skirting the cliffs where they meet the sea.
  ...mesaContour(MAIN, -4.2, 10).flatMap((p, i) => {
    if (mesaShape(YARD, p.x, p.z) < 1.25 || jitter(i, 3) < -.35) return [];
    const r = 2.3 + (jitter(i, 4) + 1) * .9;
    return [{ x: p.x + p.nx * jitter(i, 5) * 1.5, z: p.z + p.nz * jitter(i, 5) * 1.5, r, h: .8 + (jitter(i, 6) + 1) * 1.4, yaw: jitter(i, 7) * Math.PI }];
  }),
  ...mesaContour(YARD, -4.5, 12).flatMap((p, i) => {
    if (mesaShape(MAIN, p.x, p.z) < 1.25 || Math.hypot(p.x - RAMP.x1, p.z - RAMP.z1) < 14 || jitter(i, 8) < -.2) return [];
    return [{ x: p.x, z: p.z, r: 2 + (jitter(i, 9) + 1) * .7, h: .6 + (jitter(i, 10) + 1) * .9, yaw: jitter(i, 11) * Math.PI }];
  }),
  // A broken crown of rock behind the beacon, higher than the plateau.
  ...[[-.42, .62, 4.2, 25.5], [-.22, .74, 3.4, 24.6], [-.58, .4, 3.6, 24.2], [-.6, .64, 2.6, 26.4]].map(([fx, fz, r, h], i) => ({ ...onTop(fx!, fz!), r: r!, h: h!, yaw: jitter(i, 12) * Math.PI })),
];

const PROPS: Prop[] = [];

// Palisade walls ring the yard just inside its rim, broken by the gate and the cliff. Modules
// are laid out from the gate in both directions so the first ones butt against its stub walls
// (gate-local x = ±3) and the run stays contiguous; a gap would let walkers bypass the gate.
// Model +z (outer face) turns along the outward normal; the braced face looks into the yard.
const STAIR_CLEAR = { minX: stairEnd - 2, maxX: STAIR_X0 + 2, minZ: STAIR.z - STAIR_WIDTH / 2 - 2, maxZ: STAIR.z + STAIR_WIDTH / 2 + 2 };
{
  const rim = mesaContour(YARD, 3, .25);
  let gateIndex = 0;
  rim.forEach((p, i) => { if (Math.hypot(p.x - GATE.x, p.z - GATE.z) < Math.hypot(rim[gateIndex]!.x - GATE.x, rim[gateIndex]!.z - GATE.z)) gateIndex = i; });
  for (const dir of [1, -1]) {
    for (let arc = 5; arc < rim.length * .25 / 2; arc += 4) {
      const p = rim[(gateIndex + dir * Math.round(arc / .25) + rim.length * 4) % rim.length]!;
      if (mesaShape(MAIN, p.x, p.z) < 1.12) break;
      if (p.x > STAIR_CLEAR.minX && p.x < STAIR_CLEAR.maxX && p.z > STAIR_CLEAR.minZ && p.z < STAIR_CLEAR.maxZ) continue;
      PROPS.push({ model: "palisade", x: p.x, z: p.z, yaw: Math.atan2(p.nx, p.nz) });
    }
  }
}

// Scavenged rim walls on the clifftop: runs of four modules with gaps, clear of the stair head.
mesaContour(MAIN, 3.4, 4).forEach((p, i) => {
  if (i % 9 > 3 || Math.hypot(p.x - stairEnd, p.z - STAIR.z) < 9) return;
  PROPS.push({ model: "palisade", x: p.x, z: p.z, yaw: Math.atan2(p.nx, p.nz) });
});

// Sharpened stakes in the shallows below the yard walls, pointing out to sea.
mesaContour(YARD, -6.5, 7).forEach((p, i) => {
  if (i % 2 || mesaShape(MAIN, p.x, p.z) < 1.2) return;
  if (Math.hypot(p.x - RAMP.x1, p.z - RAMP.z1) < 14 || Math.abs(p.x - RAMP.x1) < 6) return;
  PROPS.push({ model: "stakes", x: p.x, z: p.z, yaw: Math.atan2(p.nx, p.nz) + jitter(i, 2) * .3 });
});

const at = (model: Prop["model"], p: { x: number; z: number }, yaw = 0): Prop => ({ model, ...p, yaw });
const inYard = (fx: number, fz: number) => ({ x: YARD.x + fx * YARD.rx, z: YARD.z + fz * YARD.rz });
PROPS.push(
  // Yard: shacks against the walls, stores, a cookfire, banners and a corner watch scaffold.
  at("fortShack", inYard(-.5, -.42)), at("fortShack", inYard(.6, -.05), Math.PI / 2), at("fortShack", inYard(.38, .08), Math.PI),
  at("crateStack", inYard(-.33, -.5)), at("crateStack", inYard(.58, .38), Math.PI / 2), at("crateStack", inYard(.32, -.2)),
  at("campfire", inYard(-.05, -.25)), { ...at("scaffoldTower", inYard(.74, .22), Math.PI / 2), id: "bastion-yard-watch" }, at("scaffoldTower", inYard(-.62, -.28), -Math.PI / 2),
  at("bannerPole", { x: GATE.x - 4.6, z: GATE.z + 1.6 }), at("bannerPole", { x: GATE.x + 4.4, z: GATE.z + 2.6 }), at("bannerPole", inYard(-.6, -.15)),
  // Clifftop: the keeper's shack, stores by the beacon, watch scaffolds on the corners.
  at("fortShack", onTop(-.55, -.1)), at("fortShack", onTop(.45, .4), Math.PI / 2),
  at("crateStack", onTop(-.3, .25)), at("crateStack", onTop(-.48, .42), Math.PI / 2), at("crateStack", { x: stairEnd - 3, z: STAIR.z + 5.5 }),
  at("campfire", onTop(-.1, .3)), at("scaffoldTower", onTop(.62, .62), 0), at("scaffoldTower", onTop(-.66, -.55), Math.PI), at("scaffoldTower", onTop(.66, -.4), Math.PI / 2),
  at("bannerPole", { x: stairEnd + 1, z: STAIR.z + 6 }), at("bannerPole", onTop(.3, .66)), at("bannerPole", onTop(-.7, .2)),
  // Shallows: stake fences round the stilt lookout.
  at("stakes", { x: LOOKOUT.x - 9, z: LOOKOUT.z - 6 }, -2.2), at("stakes", { x: LOOKOUT.x + 8, z: LOOKOUT.z - 8 }, 2.4), at("stakes", { x: LOOKOUT.x + 2, z: LOOKOUT.z + 10 }, .3),
  { model: "crateStack", x: RAMP.x1 + .6, z: DOCK.z0 + 1.2, yaw: Math.PI / 2, y: DOCK.top },
  // Gate levers, in the gate's frame (+z faces out to sea): one outside, one in the yard.
  { model: "gateLever", on: "bastion-gate", x: -2.9, z: 1.3, yaw: Math.PI },
  { model: "gateLever", on: "bastion-gate", x: 2.9, z: -1.3, yaw: 0 },
  // Plunder crates to smash open: around the yard and clifftop, and up on the decks.
  at("lootCrate", inYard(-.2, -.55), .4), at("lootCrate", inYard(.5, -.32), -.3), at("lootCrate", inYard(-.45, .05), 1.1),
  at("lootCrate", onTop(-.25, .55), .2), at("lootCrate", onTop(.2, -.3), -.6), at("lootCrate", onTop(-.6, -.25), 2.2),
  { model: "lootCrate", on: "bastion-beacon", x: 1.3, z: 1.3, y: 16, yaw: .3 },
  { model: "lootCrate", on: "bastion-lookout", x: .55, z: .7, y: 11.5, yaw: -.2 },
  { model: "lootCrate", on: "bastion-yard-watch", x: -.7, z: .6, y: 7, yaw: .5 },
  // Mounted telescopes look out to sea from the beacon and the stilt lookout.
  { model: "mountedTelescope", on: "bastion-beacon", x: -1.4, z: -1.9, y: 16, yaw: Math.PI },
  { model: "mountedTelescope", on: "bastion-lookout", x: 1, z: -2.2, y: 11.5, yaw: Math.PI },
);

const BEACON = onTop(-.32, .48);
const STAIR_HEAD = { x: stairEnd + STAIR_LANDING / 2, z: STAIR.z + 4 };

const DIG = { yard: inYard(.18, -.4), beacon: onTop(.05, .5), rim: onTop(.38, -.05) };

// Weathered outcrops stand proud of the rim between the walls, breaking the skyline.
mesaContour(MAIN, 1.6, 11).forEach((p, i) => {
  const clear = (q: { x: number; z: number }, d: number) => Math.hypot(q.x - p.x, q.z - p.z) > d;
  if (jitter(i, 13) < -.2 || !clear(STAIR_HEAD, 10) || !clear(DIG.beacon, 6) || !clear(DIG.rim, 6)) return;
  if (!PROPS.every((q) => clear(q, q.model === "palisade" ? 4.5 : 5))) return;
  BOULDERS.push({ x: p.x, z: p.z, r: 1.6 + (jitter(i, 14) + 1) * .5, h: stairTop + .9 + (jitter(i, 15) + 1) * .8, yaw: jitter(i, 16) * Math.PI });
});

const TRAILS = [
  { points: [STAIR_HEAD, onTop(-.2, -.1), onTop(-.28, .3), BEACON], width: 2.2 },
  { points: [onTop(-.2, -.1), onTop(-.48, -.12)], width: 1.6 },
  { points: [onTop(-.28, .3), onTop(.1, .5), onTop(.42, .42), onTop(.6, .58)], width: 1.5 },
  { points: [{ x: GATE.x, z: GATE.z }, inYard(.05, -.3), inYard(-.2, .05), { x: STAIR.x + 1, z: STAIR.z - 1 }], width: 3 },
  { points: [inYard(.05, -.3), inYard(.5, -.05)], width: 2 },
];

/** Island supply barrel, just inside the gate (within anchoring range of the jetty). */
export const BASTION_BARREL = { x: GATE.x - 3, z: GATE.z + 5 };

export const BASTION: Island = {
  id: "bastion",
  name: "Splinter Bastion",
  kind: "island",
  theme: "tropical",
  cx: CX,
  cz: CZ,
  // The base landmass is a drowned sand shelf: the cliffs rise straight out of a shallow lagoon.
  radius: 62,
  shore: -1.6,
  plateau: -1.6,
  peak: -1.6,
  seed: 131,
  harmonics: [
    { k: 1, amp: .15, phase: 2.79 },
    { k: 3, amp: .04, phase: 2 },
    { k: 5, amp: .02, phase: .6 },
  ],
  bumps: [],
  mesas: [MAIN, YARD],
  trails: TRAILS,
  ramps: [RAMP],
  stairs: [STAIR],
  docks: [DOCK],
  boulders: BOULDERS,
  props: PROPS,
  landmarks: [
    { id: "bastion-beacon", kind: "beaconTower", label: "Beacon Tower", ...BEACON },
    { id: "bastion-gate", kind: "stockadeGate", label: "Stockade Gate", x: GATE.x, z: GATE.z, yaw: Math.atan2(GATE_DIR.x, GATE_DIR.z) },
    { id: "bastion-lookout", kind: "seaLookout", label: "Stilt Lookout", ...LOOKOUT },
  ],
  digSpots: [
    { id: "bastion-1", ...DIG.yard, landmark: "bastion-gate", hint: "Inside the stockade, between the cookfire and the gate." },
    { id: "bastion-2", ...DIG.beacon, landmark: "bastion-beacon", hint: "On the clifftop, east of the beacon fire." },
    { id: "bastion-3", ...DIG.rim, landmark: "bastion-lookout", hint: "On the clifftop's seaward edge, looking out to the stilt lookout." },
  ],
  trees: [
    // Clifftop grove, thickest toward the beacon.
    ...[[-.7, .45], [-.5, .78], [-.2, .62], [.1, .72], [.25, .5], [-.05, .12], [.15, -.05], [-.42, -.38], [-.2, -.3],
      [.45, .15], [-.75, -.1], [-.32, .82], [.5, -.2], [.05, -.55], [-.62, .05], [.55, .6]].map(([fx, fz]) => onTop(fx!, fz!)),
    // Yard.
    inYard(.48, -.5), inYard(-.75, -.25), inYard(.2, .32),
  ],
};
